import React from "react";
import Loader from "../../../common/loader";
import CoOwnerCardView from "./coOwnerCardView";
import CoOwnerTableView from "./coOwnerTableView";
import AddCoOwnerModal from "./addCoOwner";
import { getLoginUserData } from "../../../services/account.service";
import {
  blockCoOwner,
  deleteCoOwnerById,
  getCoOwners,
} from "../../../services/co-owner.service";
import {
  IconButton,
  Button,
  Paper,
  InputBase,
  Snackbar,
  Alert,
  Pagination,
  Typography,
  Box,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonAddAlt as PersonAddAltIcon,
  Email as EmailIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { CoOwner, Business } from "../../../validation/business.validation";
import { coOwnerMessages } from "../../../constants/message.constant";
import InviteCoOwnerDialog from "./inviteCoOwner";

const defaultBusiness: Business = {
  name: "",
  ein: "",
  email: "",
  phone: "",
  address: "",
  synonyms: "",
  website: "",
  id: "",
};

const CoOwners: React.FC = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [view, setView] = React.useState<"grid" | "table">("grid");
  const [openModal, setOpenModal] = React.useState(false);
  const [user, setUser] = React.useState<Business>(defaultBusiness);
  const [coOwners, setCoOwners] = React.useState<CoOwner[]>([]);
  const [editCoOwnerDetails, setEditCoOwnerDetails] =
  React.useState<CoOwner | null>(null);
  const [query, setQuery] = React.useState({
    page: 1,
    limit: 25,
    search: "",
    businessId: "",
  });
  const [paginationData, setPaginationData] = React.useState({
    page: 1,
    limit: 25,
    total: 0,
  });
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });
  const [openInviteDialog, setInviteOpenDialog] = React.useState(false);

  const totalPages = Math.ceil(paginationData.total / paginationData.limit);

  React.useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const user = await getLoginUserData();
        if (user?.id) {
          setUser(user);
          const updatedQuery = { ...query, businessId: user.id };
          setQuery(updatedQuery);
          await fetchCoOwners(updatedQuery);
        }
      } catch (error: any) {
        showSnackbar("error", error?.message || "Failed to load coOwners.");
      } finally {
        setIsLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.businessId) fetchCoOwners(query);
    }, 700);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.search]);

  const fetchCoOwners = async (payload: any) => {
    try {
      setIsLoading(true);
      const response = await getCoOwners(payload);
      setCoOwners(response.data);
      setPaginationData({
        page: response.page,
        limit: response.limit,
        total: response.total,
      });
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to fetch coOwners.");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteCoOwner = async (coOwner: CoOwner) => {
    setIsLoading(true);
    try {
      if (coOwner.id) {
        await deleteCoOwnerById(coOwner.id);
        showSnackbar("success", coOwnerMessages.deleteCoOwner);
        fetchCoOwners(query);
      }
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to delete coOwner.");
    } finally {
      setIsLoading(false);
    }
  };

  const showSnackbar = (
    severity: "success" | "error" | "info",
    message: string
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery((prev) => ({ ...prev, search: event.target.value, page: 1 }));
  };

  const handlePageChange = (_: any, value: number) => {
    const updatedQuery = { ...query, page: value };
    setQuery(updatedQuery);
    fetchCoOwners(updatedQuery);
  };

  const addModalClose = (action?: string) => {
    setOpenModal(false);
    if (action === "reload") {
      fetchCoOwners(query);
    }
    setEditCoOwnerDetails(null);
  };

  const editCoOwner = async (coOwner: CoOwner) => {
    if (coOwner) {
      setOpenModal(true);
      setEditCoOwnerDetails(coOwner);
    }
  };

  const deactivateCoOwner = async (coOwner: CoOwner) => {
    try {
      setIsLoading(true);
      if (coOwner) {
        const status = !coOwner.isActive;
        await blockCoOwner(coOwner.id, { isActive: status });
        showSnackbar(
          "success",
          `${`${(coOwner as any).firstName || ''} ${(coOwner as any).middleName || ''} ${(coOwner as any).lastName || ''}`.trim()} ${
            coOwner.isActive ? "deactivate" : "active"
          } successfully`
        );
        fetchCoOwners(query);
      }
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to fetch coOwners.");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 py-2 px-4">
          <Paper
            component="form"
            className="flex items-center border border-gray-300 rounded-full px-2"
            elevation={0}
            sx={{
              borderRadius: 2,
              py: 0.5,
              fontWeight: 600,
              minWidth: { xs: '100%', sm: '350px' },
              maxWidth: { xs: '100%', sm: '450px' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <SearchIcon className="text-gray-400 ml-2" />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search co-owner"
              value={query.search}
              onChange={handleSearchChange}
            />
          </Paper>

          <div className="flex items-center flex-wrap gap-3">
            <Button
              variant="contained"
              startIcon={<PersonAddAltIcon />}
              onClick={() => setOpenModal(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Add Co-owner
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => setInviteOpenDialog(true)}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Invite Co-owner
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
              <IconButton
                onClick={() => setView("grid")}
                className={`!p-2 ${view === "grid" ? "bg-gray-100" : ""}`}
              >
                <ViewModuleIcon
                  className={view === "grid" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
              <IconButton
                onClick={() => setView("table")}
                className={`!p-2 ${view === "table" ? "bg-gray-100" : ""}`}
              >
                <ViewListIcon
                  className={view === "table" ? "text-black" : "text-gray-500"}
                />
              </IconButton>
            </div>
          </div>
        </div>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {coOwners.length === 0 ? (
              <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
                <div className="flex items-center justify-center mb-4">
                  <div className="animate-pulse">
                    <SearchRoundedIcon
                      className="text-gray-400"
                      sx={{ fontSize: 48 }}
                    />
                  </div>
                </div>
                <Typography variant="h6" className="mt-4 text-gray-700">
                  No Co-owners Found
                </Typography>
                <Typography variant="body2" className="text-gray-500">
                  You can try adjusting your search or add a new co-owner to get
                  started.
                </Typography>
              </Box>
            ) : view === "grid" ? (
              <CoOwnerCardView
                coOwners={coOwners}
                onDelete={deleteCoOwner}
                onEdit={editCoOwner}
                onBlock={deactivateCoOwner}
              />
            ) : (
              <CoOwnerTableView
                coOwners={coOwners}
                onDelete={deleteCoOwner}
                onEdit={editCoOwner}
                onBlock={deactivateCoOwner}
              />
            )}

            {coOwners.length > 0 && (
              <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
                  <Pagination
                    count={totalPages}
                    page={paginationData.page}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="medium"
                  />
                <div className="order-1 md:order-2 text-sm text-gray-600 flex flex-wrap items-center gap-x-1.5 gap-y-1 md:gap-x-2">
                  <span>Showing</span>
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-900 font-medium">
                    {(paginationData.page - 1) * paginationData.limit + 1}
                  </span>
                  <span>to</span>
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-900 font-medium">
                    {Math.min(
                      paginationData.page * paginationData.limit,
                      paginationData.total
                    )}
                  </span>
                  <span>of</span>
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-900 font-medium">
                    {paginationData.total}
                  </span>
                  <span>results</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <AddCoOwnerModal
        open={openModal}
        handleClose={addModalClose}
        userId={user.id}
        editMode={editCoOwnerDetails ? true : false}
        coOwnerData={editCoOwnerDetails || undefined}
      />

      <InviteCoOwnerDialog
        open={openInviteDialog}
        onClose={() => setInviteOpenDialog(false)}
        user={user}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CoOwners;
